import * as Dialog from "@radix-ui/react-dialog";
import { useState, useEffect, useMemo } from "react";
import { X, Phone, Mail, MapPin, Users, IdCard } from "lucide-react";

export default function AssignVolunteerModal({
  isOpen,
  onClose,
  driverId,
  driver,
  teams = [],
  fetchDriverMultipleTeams,
  updateDriverActiveTeam,
    deleteDriverMultipleTeams, 
  onNext,
}) {
  const [driverTeams, setDriverTeams] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [activeTeamToUpdate, setActiveTeamToUpdate] = useState("");

  const [loading, setLoading] = useState(false);

  const initials = useMemo(() => {
    const name = driver?.fullName || driver?.username || "";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const a = (parts[0]?.[0] || "").toUpperCase();
    const b = (parts[1]?.[0] || "").toUpperCase();
    return a + b || "D";
  }, [driver]);

  const photoUrl = driver?.profile_photo?.trim?.() || "";

  useEffect(() => {
    if (!isOpen || !driverId) return;

    const loadTeams = async () => {
      setLoading(true);
      const apiTeams = await fetchDriverMultipleTeams(driverId);

      if (Array.isArray(apiTeams)) {
        setDriverTeams(apiTeams);

        const allApiTeamIds = apiTeams.map((t) =>
          String(t.teamId || t.team_id || t.id).trim(),
        );

        setSelectedTeamIds([...new Set(allApiTeamIds)]);
      } else {
        setDriverTeams([]);
        setSelectedTeamIds([]);
      }
      setLoading(false);
    };

    loadTeams();
  }, [isOpen, driverId]);

  const toggleTeam = (id) => {
    const sid = String(id).trim();
    setSelectedTeamIds((prev) =>
      prev.includes(sid) ? prev.filter((t) => t !== sid) : [...prev, sid],
    );
  };

  const mergedTeamsForLeft = useMemo(() => {
    return driverTeams.filter((t) =>
      selectedTeamIds.includes(String(t.teamId || t.team_id || t.id).trim()),
    );
  }, [driverTeams, selectedTeamIds]);

  const handleNext = () => {
    if (!driverId) return alert("Driver missing");
    if (selectedTeamIds.length === 0) return alert("Select at least one team");

    const selectedTeamObjects = selectedTeamIds.map((id) => {
      const apiTeam = driverTeams.find(
        (t) => String(t.teamId || t.team_id || t.id).trim() === id,
      );

      if (apiTeam) {
        return {
          id,
          teamId: id,
          team_name: apiTeam.teamName || apiTeam.team_name,
          teamName: apiTeam.teamName || apiTeam.team_name,
          isPrimary: apiTeam.isPrimary,
          isActive: apiTeam.isActive,
        };
      }

      const team = teams.find((t) => String(t.id).trim() === id);
      return {
        id,
        teamId: id,
        team_name: team?.team_name || team?.teamName || "Unknown",
        teamName: team?.team_name || team?.teamName || "Unknown",
        isPrimary: "N",
        isActive: "Y",
      };
    });

    onNext(driverId, selectedTeamIds, selectedTeamObjects);
  };

  const handleDeleteTeam = async () => {
  if (!activeTeamToUpdate) {
    alert("Please select a team first");
    return;
  }

  const confirm = window.confirm("Are you sure you want to delete this team?");
  if (!confirm) return;

  setLoading(true);

  const success = await deleteDriverMultipleTeams({
    driverId,
    adminId: driver?.adminId || "",
    teamIds: [activeTeamToUpdate],
  });

  if (success) {
    // refresh teams list after delete
    const updated = await fetchDriverMultipleTeams(driverId);
    setDriverTeams(Array.isArray(updated) ? updated : []);
    setSelectedTeamIds((prev) =>
      prev.filter((id) => id !== activeTeamToUpdate)
    );
    setActiveTeamToUpdate("");
  }

  setLoading(false);
};

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />

        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95%] max-w-5xl bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
          {/* ========== HEADER (NO PADDING ON CONTAINER) ========== */}
          <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-blue-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700">
                <IdCard size={18} />
              </div>
              <div>
                <Dialog.Title className="text-base font-semibold text-gray-900">
                  Driver Details
                </Dialog.Title>
                <p className="text-xs text-gray-600">
                  {driver?.driverId} • {driver?.teamName || "—"} •{" "}
                  {driver?.status || "—"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Dialog.Close asChild>
                <button
                  className="p-2 rounded-lg hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </Dialog.Close>
            </div>
          </div>

          <div className="p-4 max-h-[85vh] overflow-y-auto space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 border rounded-xl p-4 bg-gradient-to-r from-blue-50 to-white">
              {/* LEFT CARD */}
              <div className="rounded-2xl overflow-hidden border shadow-sm bg-white">
                <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs opacity-90">PACKKY • DRIVER</div>
                      <div className="text-lg font-semibold leading-tight mt-1">
                        {driver?.fullName || driver?.username || "—"}
                      </div>
                      <div className="text-xs opacity-95 mt-1">
                        Code:{" "}
                        <span className="font-semibold">
                          {driver?.driverId}
                        </span>
                      </div>
                    </div>

                    <div className="w-14 h-14 rounded-2xl bg-white/20 border border-white/30 overflow-hidden flex items-center justify-center">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt="Driver"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white font-bold">
                          {initials}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    <Tag label={`Tier: ${driver?.tier || "—"}`} />
                    <Tag label={`Duty: ${driver?.dutyStatus || "—"}`} />
                    <Tag label={`Type: ${driver?.driver_type || "—"}`} />
                    <Tag label={`Team: ${driver?.teamName || "—"}`} />
                  </div>
                </div>
              </div>

              {/* RIGHT CARD */}
              <div className="rounded-2xl border shadow-sm bg-white p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Contact & Location
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <MiniRow
                    icon={<Phone size={14} className="text-blue-600" />}
                    label="Phone"
                    value={driver?.phone || "—"}
                  />
                  <MiniRow
                    icon={<Mail size={14} className="text-blue-600" />}
                    label="Email"
                    value={driver?.email || "—"}
                  />
                  <MiniRow
                    icon={<MapPin size={14} className="text-blue-600" />}
                    label="Location"
                    value={driver?.location_address || "—"}
                  />
                </div>
              </div>
            </div>

            <Dialog.Title className="text-lg font-bold flex items-center gap-2">
              <Users size={18} /> Assign Multiple Teams
            </Dialog.Title>

            <div className="flex gap-4 flex-1 overflow-hidden">
              {/* LEFT BOX */}
              <div className="flex-1 border rounded p-4 overflow-y-auto flex flex-col">
                <h3 className="font-semibold mb-2">Driver's Teams</h3>

                {loading ? (
                  <p className="text-gray-500">Loading teams...</p>
                ) : mergedTeamsForLeft.length === 0 ? (
                  <p className="text-gray-500">No teams selected</p>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto">
                      {mergedTeamsForLeft.map((t) => {
                        const teamId = String(
                          t.teamId || t.team_id || t.id,
                        ).trim();
                        const isSelected = activeTeamToUpdate === teamId;

                        return (
                          <div
                            key={teamId}
                            className={`p-2 border-b cursor-pointer transition-colors ${
                              isSelected
                                ? "bg-blue-50 border-l-4 border-l-blue-500"
                                : ""
                            }`}
                            onClick={() => setActiveTeamToUpdate(teamId)}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="activeTeam"
                                checked={isSelected}
                                onChange={() => setActiveTeamToUpdate(teamId)}
                                onClick={(e) => e.stopPropagation()}
                              />

                              <div className="flex-1">
                                <div className="font-medium">
                                  {t.teamName || t.team_name}
                                </div>

                                <div className="flex items-center gap-2 text-xs mt-1">
                                  <span
                                    className={`px-2 py-0.5 rounded-full font-semibold ${
                                      t.isPrimary === "Y"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-gray-100 text-gray-700"
                                    }`}
                                  >
                                    Primary:{" "}
                                    {t.isPrimary === "Y" ? "Yes" : "No"}
                                  </span>

                                  <span
                                    className={`px-2 py-0.5 rounded-full font-semibold ${
                                      t.isActive === "Y"
                                        ? "bg-blue-100 text-blue-700"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    Active: {t.isActive === "Y" ? "Yes" : "No"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                   <div className="mt-3 flex justify-end gap-2">
  <button
    disabled={!activeTeamToUpdate}
    className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
    onClick={handleDeleteTeam}
  >
    Delete Team
  </button>

  <button
    disabled={!activeTeamToUpdate}
    className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
    onClick={async () => {
      await updateDriverActiveTeam({
        adminId: driver?.adminId || "",
        driverId: driverId,
        teamId: activeTeamToUpdate,
      });

      await fetchDriverMultipleTeams(driverId);
      setActiveTeamToUpdate("");
    }}
  >
    Activate Team
  </button>
</div>

                  </>
                )}
              </div>

              {/* RIGHT BOX */}
              <div className="flex-1 border rounded p-4 flex flex-col h-[400px]">
                <h3 className="font-semibold mb-2">Select More Teams</h3>

                <div className="flex-1 overflow-y-auto max-h-[320px]">
                  {teams.map((t) => {
                    const id = String(t.id).trim();
                    const isChecked = selectedTeamIds.includes(id);

                    return (
                      <div
                        key={id}
                        className={`p-2 border-b cursor-pointer ${
                          isChecked
                            ? "bg-blue-50 border-l-4 border-l-blue-500"
                            : ""
                        }`}
                        onClick={() => toggleTeam(id)}
                      >
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleTeam(id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mr-2"
                          />
                          <span className="flex-1">
                            {t.team_name || t.teamName}
                          </span>
                          <span className="text-xs text-gray-400">({id})</span>
                        </label>
                      </div>
                    );
                  })}
                </div>

              </div>
            </div>

            {/* ========== ACTION BUTTONS ========== */}
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleNext}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save Teams
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ---------- HELPERS ---------- */

function Tag({ label }) {
  return (
    <span className="text-[11px] px-2 py-1 rounded-full bg-white/15 border border-white/25">
      {label}
    </span>
  );
}

function MiniRow({ icon, label, value }) {
  return (
    <div className="rounded-xl border bg-white p-3 flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] text-gray-500">{label}</div>
        <div className="text-sm font-semibold truncate">{value || "—"}</div>
      </div>
    </div>
  );
}
